/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyMessage from 'components/@common/EmptyResultSetMessage';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import { ProductWithCurriculum } from 'types';
import { Student } from 'types/schema-generated-types';
import StudentKeyboardingSettings from './components/KeyboardingSettings';
import StudentGssSettings from './components/GssSettings';
import StudentHwtSettings from './components/HwtSettings';
import StudentWdtSettings from './components/WdtSettings';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';

interface SettingsContainerProps {
  classroomId: number;
  student: Student;
  productsWithCurriculum: ProductWithCurriculum[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: string | null;
  name: string;
}

interface A11yProps {
  id: string;
  'aria-controls': string;
}

const PRODUCT_SETTINGS = [
  { productFamilyCode: 'GSS', component: StudentGssSettings },
  { productFamilyCode: 'KEY', component: StudentKeyboardingSettings },
  { productFamilyCode: 'WDT', component: StudentWdtSettings },
  { productFamilyCode: 'HWT', component: StudentHwtSettings }
];

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    height: '100%'
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    width: theme.spacing(28.75)
  },
  tabPanel: {
    margin: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(42.75)}px)`
  },
  tabPanelBox: {
    display: 'flex',
    flexDirection: 'column'
  },
  tabButton: {
    '& span': {
      textAlign: 'left',
      alignItems: 'flex-start'
    }
  }
}));

const TabPanel: React.FC<TabPanelProps> = props => {
  const { children, name, value, index, ...other } = props;
  const classes = useStyles();

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== name}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
      {...other}
    >
      {value === name && <Box className={classes.tabPanelBox}>{children}</Box>}
    </Typography>
  );
};

const a11yProps = (index: number): A11yProps => ({
  id: `vertical-tab-${index}`,
  'aria-controls': `vertical-tabpanel-${index}`
});

type SettingsContainerActionRouteParams = {
  classroomId: number;
  studentId: number;
  tab?: string;
  setting?: string;
};

const SettingsContainer: React.FC<SettingsContainerProps> = ({
  classroomId,
  student,
  productsWithCurriculum
}) => {
  const classes = useStyles();
  const { t } = useTranslation('Student');
  const { t: tProductFamily } = useTranslation('ProductFamily');
  const params = useParseActionRouteParams<SettingsContainerActionRouteParams>();

  const productsWithSettings = useMemo(
    () =>
      PRODUCT_SETTINGS.reduce((acc, x) => {
        const product = productsWithCurriculum.find(
          ({ product }) => product.family.code === x.productFamilyCode
        );
        return product ? [...acc, x] : acc;
      }, [] as { productFamilyCode: string; component: any }[]),
    [productsWithCurriculum]
  );

  const [selectedTab, setSelectedTab] = useState<string>(
    params?.tab?.toUpperCase() ?? productsWithSettings[0]?.productFamilyCode
  );

  const [expanded, setExpanded] = useState<string | null>(params?.setting ?? null);

  const handleChangeExpanded = React.useCallback(
    (panel: string | null) => (): void => {
      setExpanded(expanded === panel ? null : panel);
    },
    [expanded, setExpanded]
  );

  const handleChange = (event: React.ChangeEvent<{}>, productFamilyCode: string): void => {
    setSelectedTab(productFamilyCode);
    handleChangeExpanded(null)();
  };

  const tabProps = React.useMemo(() => {
    return {
      student,
      classroomId,
      expanded,
      handleChangeExpanded
    };
  }, [student, classroomId, expanded, handleChangeExpanded]);

  const settings = useMemo(
    () =>
      PRODUCT_SETTINGS.reduce((acc, settings) => {
        const productWithCurriculum = productsWithCurriculum.find(
          ({ product }) => product.family.code === settings.productFamilyCode
        );

        const curriculum = productWithCurriculum?.productCurriculum;
        if (!curriculum) return acc;
        return [...acc, { ...settings, productCurriculumCode: curriculum.code }];
      }, [] as { productFamilyCode: string; productCurriculumCode: string; component: any }[]),
    [productsWithCurriculum]
  );

  if (productsWithSettings.length === 0) {
    return (
      <EmptyMessage
        style={{ marginTop: '12px' }}
        message={t('No settings available.')}
        subtitle={t(
          `The student either has no license assigned or is licensed for products with no settings.`
        )}
      />
    );
  }

  return (
    <div className={classes.root}>
      <Tabs
        orientation="vertical"
        variant="standard"
        textColor="secondary"
        value={selectedTab}
        onChange={handleChange}
        aria-label={t('Student apps')}
        className={classes.tabs}
      >
        {settings.map(({ productFamilyCode }, index) => (
          <Tab
            key={`tab-${productFamilyCode}`}
            label={tProductFamily(productFamilyCode)}
            className={classes.tabButton}
            value={productFamilyCode}
            {...a11yProps(index)}
          />
        ))}
      </Tabs>
      {settings.map(
        ({ productFamilyCode, productCurriculumCode, component: SettingsPanel }, index) => (
          <TabPanel
            key={`${productFamilyCode}-settings`}
            value={selectedTab}
            name={productFamilyCode}
            index={index}
          >
            <SettingsPanel {...tabProps} productCurriculumCode={productCurriculumCode} />
          </TabPanel>
        )
      )}
    </div>
  );
};

export default SettingsContainer;
